import React, { useMemo } from 'react';
import { Paper, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LatencyData, LatencyMeasurePoint } from 'core/models/latency';
import { DisplayType } from './LatencyDisplayType';
import { dateToTimeString } from 'services/utility/time';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

export type LatencyTableProps = {
    displayType: DisplayType;
    points: LatencyMeasurePoint;
    currentMeasurements: {
        tag: string;
        measure: (LatencyData | undefined)[];
    }[] | undefined;
}

export const LatencyTable = (props: LatencyTableProps) => {
    const { points, displayType } = props;
    const caption = useMemo(() => getCaption(displayType), [displayType]);

    return (
        <TableContainer component={Paper}>
            <Table sx={{
                minWidth: 200,
            }} aria-label='simple table'>
                <LatencyPanelHeader points={points} />
                <LatencyPanelBody {...props} displayType={displayType} />
                <caption>{caption}</caption>
            </Table>
        </TableContainer>
    );
};

function LatencyPanelBody(props: LatencyTableProps) {
    const { points, currentMeasurements, displayType } = props;

    return (
        <TableBody>
            {currentMeasurements?.map((currentMeasurement, index) => {
                return (
                    <TableRow key={index}>
                        <TableCell component='th' scope='row'>{currentMeasurement.tag}</TableCell>
                        <TableCell >
                            <React.Fragment>{currentMeasurement.measure.map((_, ch) => { return <React.Fragment key={ch}> Ch:{ch + 1} <br /> </React.Fragment>; })}</React.Fragment>
                        </TableCell>
                        <Measurements measurements={currentMeasurement.measure} displayType={displayType} />
                        {points.map((m, point) => {
                            return <Measurements key={`${index}${point}`} measurements={m.cameras[index].channels.map(m => m.measurement)} displayType={displayType} />;
                        })}
                    </TableRow>
                );
            })}
        </TableBody>
    );
}

type LatencyPanelHeaderProps = {
    points: LatencyMeasurePoint;
};

function LatencyPanelHeader(props: LatencyPanelHeaderProps) {
    const { points } = props;
    return (
        <TableHead>
            <TableRow>
                <StyledTableCell >Camera</StyledTableCell>
                <StyledTableCell >Ch</StyledTableCell>
                <StyledTableCell align='right'>Current</StyledTableCell>
                {points.map((p, i) => <MeasurePoint time={p.time} key={i} />)}
            </TableRow>
        </TableHead>
    );
}

type LatencyMeasurePointProps = { time: Date };

function MeasurePoint(props: LatencyMeasurePointProps) {
    const str = useMemo(() => dateToTimeString(props.time), [props.time]);
    return <StyledTableCell align='right'>{str}</StyledTableCell>;
}

type MeasurementsProps = {
    measurements?: (LatencyData | undefined)[];
    displayType: DisplayType;
}

function Measurements(props: MeasurementsProps) {
    return (<TableCell align='right' > {
        props.measurements?.map((measurement, index) => {
            return (<React.Fragment key={index}>{latencyDataToString(measurement, props.displayType)}<br /></React.Fragment>);
        })
    } </TableCell>);
}

function getCaption(displayType: DisplayType) {
    switch (displayType) {
        case 'encode':
            return ('encode time (msec)');
        case 'network':
            return ('network Latency / upstream / downstream (msec)');
        case 'total':
        default:
            return ('all / network / encode time (msec)');
    }
}

function latencyDataToString(latencyData: LatencyData | undefined, displayType: DisplayType) {
    if (!latencyData) return 'n/a';

    const { totalLatency, upstreamLatency, downstreamLatency, encodeTime } = latencyData;
    switch (displayType) {
        case 'encode':
            return `${encodeTime}`;
        case 'network':
            return `${totalLatency} / ${upstreamLatency} / ${downstreamLatency}`;
        case 'total':
        default:
            return `${isNaN(totalLatency + encodeTime) ? '-' : totalLatency + encodeTime} / ${totalLatency} / ${encodeTime}`;
    }
}
